import React from 'react';
import { ArrowRight, Check } from 'lucide-react';
import BackgroundAnimation from '../../components/BackgroundAnimation';

const AIAutomation = () => {
  const features = [
    { title: 'AI-Powered Chatbots', desc: 'Intelligent chatbots that handle customer inquiries 24/7 with natural language processing' },
    { title: 'CRM Integration', desc: 'Seamlessly connect all your tools and data sources for unified customer management' },
    { title: 'Workflow Automation', desc: 'Automate repetitive tasks and processes to save time and reduce human error' },
    { title: 'Predictive Analytics', desc: 'AI-driven insights to predict customer behavior and optimize your strategies' },
    { title: 'Lead Scoring', desc: 'Automatically score and prioritize leads based on their likelihood to convert' },
    { title: 'Custom Solutions', desc: 'Tailored automation systems designed specifically for your business needs' }
  ];

  const benefits = [
    'Reduce manual work by up to 80%',
    'Increase lead conversion by 3x',
    '24/7 customer support availability',
    'Real-time data synchronization',
    'Automated follow-up sequences',
    'Advanced reporting and analytics'
  ];

  const integrations = ['GoHighLevel', 'HubSpot', 'Salesforce', 'Zapier', 'Make.com', 'Pipedrive', 'ActiveCampaign', 'Mailchimp'];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      
      <section className="py-24" style={{ padding: '120px 7.6923% 80px' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            AI Automation &
            <span className="block" style={{ color: 'var(--brand-primary)' }}>CRM Integration</span>
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Smart Automation That Works 24/7</p>
          <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 32px' }}>
            Transform your business with intelligent automation systems that handle routine tasks, nurture leads, and provide insights while you focus on growth and strategy.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
              <span>Get Automation Quote</span>
              <ArrowRight size={20} />
            </a>
            <a href="/services" className="button-text dark-button-animate" style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>View All Services</a>
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Powerful Automation Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, idx) => (
              <div key={idx} className="p-6 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(0, 255, 209, 0.1)' }}>
                  <Check size={24} style={{ color: 'var(--brand-primary)' }} />
                </div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>{feature.title}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="display-large text-center mb-12" style={{ color: 'var(--text-primary)' }}>Measurable Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex items-center space-x-3 p-4 rounded" style={{ background: 'rgba(255, 255, 255, 0.03)' }}>
                <Check size={24} style={{ color: 'var(--brand-primary)' }} />
                <span className="body-large" style={{ color: 'var(--text-primary)' }}>{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="display-large mb-8" style={{ color: 'var(--text-primary)' }}>Integrates With Your Tools</h2>
          <p className="body-large mb-12" style={{ color: 'var(--text-secondary)' }}>We work with all major CRM and marketing platforms</p>
          <div className="flex flex-wrap justify-center gap-4">
            {integrations.map((int, idx) => (
              <div key={idx} className="px-6 py-3 rounded-lg" style={{ background: 'rgba(0, 255, 209, 0.1)', border: '1px solid var(--brand-primary)' }}>
                <span className="body-medium" style={{ color: 'var(--brand-primary)' }}>{int}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>Ready to Automate Your Business?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Let's discuss how AI can transform your operations</p>
          <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
            <span>Start Automation</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default AIAutomation;